import { useEffect, useState, useRef } from "react";
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import { Button, Form, Space, Spin, Tabs } from 'antd';
import Utilisateur from "../../../Models/Utilisateur";
import type { TabsProps } from 'antd';
import { SearchOutlined, UndoOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import SweetAlert from "sweetalert2";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import axios from "axios";
import Tab1 from "./Tab1";
import { Typography } from "@mui/material";
import { normalizeKeysToUppercase } from "../../../utilitaires/globalFunction";
import Params from "../../../Types/ParamsPicklist";
import dayjs from 'dayjs';

const JournalFinancementPage = () => {
    const auth = useAuthUser();
    const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();
    const [form] = Form.useForm();

    // --- ÉTATS ---
    const [loading, setLoading] = useState(false);
    const [glResultats, setglResultats] = useState<any[]>([]);
    const [listStatuts, setListStatuts] = useState<any[]>([]);
    const [listTypes, setListTypes] = useState<any[]>([]);
    const [listProduits, setListProduits] = useState<any[]>([]);
    const [listGestionnaires, setListGestionnaires] = useState<any[]>([]);
    const [listEtapes, setListEtapes] = useState<any[]>([]);
    const [statusLoading, setStatusLoading] = useState(false);

    // Référence pour le debounce (anti-rebond) des appels API
    const timerRef = useRef<any>(null);

    // --- CHARGEMENT INITIAL (Niveau 1) ---
    useEffect(() => {
        // 1. Initialisation des dates (Mois en cours)
        const dateDebut = dayjs().startOf('month').format('YYYY-MM-DD');
        const dateFin = dayjs().format('YYYY-MM-DD');
        form.setFieldsValue({ DATEDEBUT: dateDebut, DATEFIN: dateFin });

        // 2. Paramètres des listes (Utilisation de la session Perss.codage)
        const pType: Params = { CodeAgence: '', Table: "TypeFinancement", Colonne1: "CodeFinancement", Colonne2: "Libelle", Colagence: Perss.codage, Choix: 22 };
        const pGest: Params = { CodeAgence: '', Table: "GESTIONNAIRE", Colonne1: "CODGES", Colonne2: "LIBELLE", Colagence: Perss.codage, Choix: 22 };
        const pStatuts: Params = { CodeAgence: '', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", colfiltre: 'RUBRIQ', valfiltre: 'STATUT_FINANCE', Colagence: Perss.codage, Choix: 22 };

        loadDynamicList(pStatuts, setListStatuts);
        loadDynamicList(pType, setListTypes);
        loadDynamicList(pGest, setListGestionnaires);
    }, []);

    // --- FONCTION GÉNÉRIQUE DE CHARGEMENT DYNAMIQUE ---
    const loadDynamicList = (params: Params, setter: (data: any[]) => void) => {
        const config = {
            method: 'get',
            url: Data.ApiUrl.AnySearch,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CypherSearch': Crypter.encrypt(JSON.stringify(params))
            }
        };

        axios(config).then((res) => {
            const response = JSON.parse(Crypter.decrypt(res.data)).Value;
            if (response.StatutCode === 200) {
                const table = response.Result.Table || response.Result || [];
                const formatted = table.map((item: any) => {
                    const normalizedItem = normalizeKeysToUppercase([item])[0];
                    const valCode = normalizedItem[params.Colonne1.toUpperCase().trim()];
                    const valLibelle = normalizedItem[params.Colonne2.toUpperCase().trim()];
                    return {
                        value: valCode,
                        label: `${valCode} - ${valLibelle}`,
                        CODE: valCode,
                        LIBELLE: `${valCode} - ${valLibelle}`
                    };
                });
                setter(formatted);
            }
        }).catch(err => console.error("Erreur Picklist:", err));
    };

    // --- GESTION DE LA CASCADE (COHÉRENCE) ---
    const handleTypeChange = (value: string) => {
        form.setFieldsValue({ CODEPRODUIT: undefined, ETAPEACTUELLE: undefined, STATUT: undefined });
        setListProduits([]);
        setListEtapes([]);
        setListStatuts([]);

        if (!value) {
            setStatusLoading(false);
            return;
        }

        let nomTable = "";
        let nomColCode = "";
        let nomColLibelle = "";


        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (nomTable) {
                setStatusLoading(true);
                loadDynamicList({
                    CodeAgence: Perss.codage,
                    Table: nomTable,
                    Colonne1: nomColCode,
                    Colonne2: nomColLibelle,
                    Choix: 22
                }, (data) => {
                    setListProduits(data);
                    setStatusLoading(false);
                });
            }

            loadDynamicList({
                CodeAgence: Perss.codage,
                Table: "WorkflowEtape",
                Colonne1: "CodeEtape",
                Colonne2: "LibelleEtape",
                colfiltre: "CodeFinancement",
                valfiltre: value,
                Choix: 22
            }, setListEtapes);
        }, 300);
    };

    // --- LOGIQUE DE CONSULTATION & EXPORT ---
    const HandleConsult = (isExport: boolean = false, format: string = "PDF") => {
        const values = form.getFieldsValue();
        setLoading(true);

        const payload = {
            DateDebut: values.DATEDEBUT,
            DateFin: values.DATEFIN,
            CodeProduit: values.CODEPRODUIT || null,
            TypeFinancement: values.TYPEFINANCEMENT || null,
            CodeAgence: Perss.codage,
            Gestionnaire: values.GESTIONNAIRE || null,
            Statut: values.STATUT || null,
            EtapeActuelle: values.ETAPEACTUELLE || null,
            CodeUti: Perss.coduti,
            AgenceUser: Perss.codage,
            Export: isExport,
            Format: format
        };

        axios({
            method: 'post',
            url: Data.ApiUrl.ReadRapportFinancement,
            data: Crypter.encrypt(JSON.stringify(payload)),
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            responseType: isExport ? 'blob' : 'text'
        }).then((response) => {
            if (isExport) {
                // --- LOGIQUE TÉLÉCHARGEMENT ---
                if (response.data.type === 'application/json' || response.data.size < 500) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const decrypted = Crypter.decrypt(reader.result as string);
                            const res = JSON.parse(decrypted).Value || JSON.parse(decrypted);
                            SweetAlert.fire({ title: res.Message || "Info Export", text: res.Result, icon: "info" });
                        } catch (err) {
                            SweetAlert.fire("Erreur", "Le serveur a renvoyé une réponse invalide.", "error");
                        }
                    };
                    reader.readAsText(response.data);
                    setLoading(false);
                    return;
                }

                const mimeType = format.toUpperCase() === "PDF" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                const blob = new Blob([response.data], { type: mimeType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const ext = format.toUpperCase() === "PDF" ? "pdf" : "xlsx";
                link.setAttribute('download', `Journal_${dayjs().format('YYYYMMDD_HHmm')}.${ext}`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setLoading(false);
            }
            else {
                // --- LOGIQUE CONSULTATION (Tableau) ---
                try {
                    const decrypted = Crypter.decrypt(response.data);
                    const res = JSON.parse(decrypted).Value;
                    if (res.StatutCode === 200) {
                        const finalTable = res.Result.Table || res.Result || [];
                        const normalized = normalizeKeysToUppercase(finalTable);
                        setglResultats(normalized);

                        // Extraction des statuts à la volée
                        if (normalized.length > 0 && !payload.Statut) {
                            const statutsUniques = [...new Set(normalized.map((item: any) => item.STATUT))]
                                .filter(s => s && s !== "")
                                .map(s => ({ value: s, label: s, CODE: s, LIBELLE: s }));
                            setListStatuts(statutsUniques);
                        }
                    } else {
                        SweetAlert.fire({ title: res.Message, text: res.Result, icon: "warning" });
                        setglResultats([]);
                        setListStatuts([]);
                    }
                } catch (e) {
                    console.error("Erreur traitement tableau:", e);
                    SweetAlert.fire("Erreur", "Données illisibles", "error");
                }
                setLoading(false);
            }
        }).catch(err => {
            setLoading(false);
            console.error("Erreur Axios:", err);
            SweetAlert.fire("Erreur", "Impossible de joindre le serveur", "error");
        });
    };

    const HandleAnnul = () => {
        form.resetFields();
        setglResultats([]);
        setListProduits([]);
        setListEtapes([]);
        setListGestionnaires([]);
        const dateDebut = dayjs().startOf('month').format('YYYY-MM-DD');
        const dateFin = dayjs().format('YYYY-MM-DD');
        form.setFieldsValue({ DATEDEBUT: dateDebut, DATEFIN: dateFin });
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span><SearchOutlined /> Critères de filtrage</span>,
            children: <Tab1
                form={form}
                glResultats={glResultats}
                listStatuts={listStatuts}
                listTypes={listTypes}
                statusLoading={statusLoading}
                listProduits={listProduits}
                listGestionnaires={listGestionnaires}
                listEtapes={listEtapes}
                onTypeChange={handleTypeChange}
            />
        }
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'>
                <b> JOURNAL GÉNÉRAL DES FINANCEMENTS </b>
            </Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form form={form} onFinish={() => HandleConsult(false)} preserve>
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Form.Item className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' htmlType="submit" block style={{ backgroundColor: '#116599', color: 'white', fontWeight: 'bold' }}>Consulter</Button>
                                <Button size='large' onClick={() => HandleConsult(true, "EXCEL")} block style={{ backgroundColor: '#116599', color: 'white' }}>Exporter Excel</Button>
                                <Button size='large' onClick={() => HandleConsult(true, "PDF")} block style={{ backgroundColor: '#116599', color: 'white' }}>Exporter PDF</Button>
                                <Button size='large' onClick={HandleAnnul} block style={{ backgroundColor: '#116599', color: 'white' }}>Annuler</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <Tabs size='large' type="card" defaultActiveKey="1" items={items} />
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default JournalFinancementPage;